import { httpClient } from '@bs/core'
import { segmentsSummaryFallback } from '../../features/Loyalty/segments/segmentListDisplay'
import type {
  CreateSegmentInput,
  LoyaltySegment,
  LoyaltySegmentsList,
  SegmentCriteria,
  SegmentMember,
  SegmentMembersResponse,
  SegmentMembersSort,
  SegmentPreview,
  SegmentsSummary,
  UpdateSegmentInput,
} from '../../types/loyaltySegments'

// httpClient.json.* unwraps res.data.data → the resolved value is the inner
// `data` payload directly (SuccessResponse envelope already stripped), same as
// loyaltyMarketingApis. Wire shape matches `types/loyaltySegments.ts` verbatim,
// so payloads pass through untouched.

const segmentsUrl = (storeId: string) => `/stores/${storeId}/loyalty/segments`
const segmentMembersUrl = (storeId: string, segmentId: string) =>
  `${segmentsUrl(storeId)}/${segmentId}/members`

// The per-segment aggregates and the list-level `customers_covered` landed
// after the first segments release; an older server omits them, so they're
// zero-filled here rather than left undefined for every renderer to guard.
const normaliseSegment = (segment: LoyaltySegment): LoyaltySegment => ({
  ...segment,
  avg_ltv: segment.avg_ltv ?? 0,
  aov: segment.aov ?? 0,
  avg_orders: segment.avg_orders ?? 0,
  active_campaigns: segment.active_campaigns ?? 0,
})

// The `summary` block backing the stats strip landed after the first
// segments release; an older server omits it, so it's derived client-side
// from the (already-normalised) segments array — same maths as the mock's
// `renderStats`.
const normaliseSummary = (
  raw: Partial<SegmentsSummary> | undefined,
  segments: LoyaltySegment[],
  customersCovered: number,
): SegmentsSummary => {
  if (!raw) return segmentsSummaryFallback(segments, customersCovered)
  return {
    total_segments: raw.total_segments ?? segments.length,
    customers_covered: raw.customers_covered ?? customersCovered,
    avg_segment_size: raw.avg_segment_size ?? 0,
    weighted_avg_ltv: raw.weighted_avg_ltv ?? 0,
    vip_customers: raw.vip_customers ?? 0,
  }
}

// The server seeds the preset segments (VIP Spenders / At-Risk Members /
// New This Month) on a store's first list call.
export const getLoyaltySegments = async (
  storeId: string,
): Promise<LoyaltySegmentsList> => {
  const raw = (await httpClient.json.get(segmentsUrl(storeId))) as {
    segments?: LoyaltySegment[]
    customers_covered?: number
    summary?: Partial<SegmentsSummary>
  } | null
  const segments = (raw?.segments ?? []).map(normaliseSegment)
  const customersCovered = raw?.customers_covered ?? 0
  return {
    segments,
    customers_covered: customersCovered,
    summary: normaliseSummary(raw?.summary, segments, customersCovered),
  }
}

// Segments a customer belongs to — customer-detail pills. `customerId` is
// the Woo customer id (what segments are keyed by). Cached memberships, so
// a brand-new customer appears after the next hourly refresh.
export const getCustomerLoyaltySegments = async (
  storeId: string,
  customerId: string | number,
): Promise<LoyaltySegment[]> => {
  const raw = (await httpClient.json.get(
    `/stores/${storeId}/loyalty/customers/${encodeURIComponent(String(customerId))}/segments`,
  )) as { segments?: Partial<LoyaltySegment>[] } | null
  return (raw?.segments ?? []).map((segment) => ({
    ...segment,
    id: segment.id ?? '',
    name: segment.name ?? '',
    description: segment.description ?? '',
    color: segment.color ?? '',
    icon_key: segment.icon_key ?? '',
    kind: segment.kind ?? 'smart',
    criteria: segment.criteria ?? { match: 'all', rules: [] },
    member_count: segment.member_count ?? 0,
    avg_ltv: segment.avg_ltv ?? 0,
    aov: segment.aov ?? 0,
    avg_orders: segment.avg_orders ?? 0,
    active_campaigns: segment.active_campaigns ?? 0,
    last_evaluated_at: segment.last_evaluated_at ?? null,
    created_at: segment.created_at ?? null,
    updated_at: segment.updated_at ?? null,
  }))
}

export const createLoyaltySegment = async (
  storeId: string,
  input: CreateSegmentInput,
): Promise<LoyaltySegment> => {
  const raw = (await httpClient.json.post(segmentsUrl(storeId), input)) as {
    segment: LoyaltySegment
  }
  return normaliseSegment(raw.segment)
}

export const updateLoyaltySegment = async (
  storeId: string,
  segmentId: string,
  input: UpdateSegmentInput,
): Promise<LoyaltySegment> => {
  const raw = (await httpClient.json.put(
    `${segmentsUrl(storeId)}/${segmentId}`,
    input,
  )) as { segment: LoyaltySegment }
  return normaliseSegment(raw.segment)
}

export const deleteLoyaltySegment = async (
  storeId: string,
  segmentId: string,
): Promise<{ deleted: boolean }> =>
  (await httpClient.json.delete(`${segmentsUrl(storeId)}/${segmentId}`)) as {
    deleted: boolean
  }

// Live-evaluates the criteria without persisting anything: total match count,
// a capped customer sample, the per-tier breakdown and the store's total
// member count. The builder debounces calls to this.
export const previewLoyaltySegment = async (
  storeId: string,
  criteria: SegmentCriteria,
): Promise<SegmentPreview> => {
  const raw = (await httpClient.json.post(`${segmentsUrl(storeId)}/preview`, {
    criteria,
  })) as Partial<SegmentPreview> | null
  return {
    count: raw?.count ?? 0,
    sample: raw?.sample ?? [],
    tier_breakdown: {
      none: raw?.tier_breakdown?.none ?? 0,
      silver: raw?.tier_breakdown?.silver ?? 0,
      gold: raw?.tier_breakdown?.gold ?? 0,
      platinum: raw?.tier_breakdown?.platinum ?? 0,
    },
    total_members: raw?.total_members ?? 0,
    avg_ltv: raw?.avg_ltv ?? 0,
    aov: raw?.aov ?? 0,
    avg_orders: raw?.avg_orders ?? 0,
  }
}

// Normalises a member row's nested objects (location/orders) and nullable
// fields so an older server — or a customer with incomplete data — can't
// crash the members table.
const normaliseMember = (
  member: Partial<SegmentMember> | null | undefined,
): SegmentMember => ({
  customer_id: member?.customer_id ?? '',
  email: member?.email ?? '',
  first_name: member?.first_name ?? '',
  last_name: member?.last_name ?? '',
  location: {
    city: member?.location?.city ?? '',
    province: member?.location?.province ?? '',
  },
  orders: {
    total: member?.orders?.total ?? 0,
    online: member?.orders?.online ?? 0,
    instore: member?.orders?.instore ?? 0,
  },
  lifetime_value: member?.lifetime_value ?? 0,
  avg_order_value: member?.avg_order_value ?? 0,
  last_order_at: member?.last_order_at ?? null,
  last_seen_at: member?.last_seen_at ?? null,
  points_balance: member?.points_balance ?? 0,
  segments: member?.segments ?? [],
})

export interface GetLoyaltySegmentMembersParams {
  limit?: number
  // Opaque offset — pass back the previous response's `cursor` verbatim.
  cursor?: number | string
  sort?: SegmentMembersSort
  // Case-insensitive substring on first/last name/email, evaluated
  // server-side.
  q?: string
}

// Paginated, sorted, searchable member list for the segment detail page's
// customer table.
export const getLoyaltySegmentMembers = async (
  storeId: string,
  segmentId: string,
  params: GetLoyaltySegmentMembersParams = {},
): Promise<SegmentMembersResponse> => {
  const raw = (await httpClient.json.get(
    segmentMembersUrl(storeId, segmentId),
    {
      params: {
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
        ...(params.cursor !== undefined && params.cursor !== null
          ? { cursor: params.cursor }
          : {}),
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.q ? { q: params.q } : {}),
      },
    },
  )) as Partial<SegmentMembersResponse> | null
  return {
    members: (raw?.members ?? []).map(normaliseMember),
    total: raw?.total ?? 0,
    has_more: raw?.has_more ?? false,
    cursor: raw?.cursor ?? null,
    sort: raw?.sort ?? params.sort ?? 'ltv',
  }
}
